import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  const register = async (name, email, password, role = 'teacher') => {
    const response = await authAPI.register({ name, email, password, role });
    const data = response.data;
    
    // Check if pending approval
    if (data.pending_approval) {
      return { pending_approval: true, user: data.user };
    }
    
    // If approved (first user / super_admin), log them in
    if (data.token) {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
    }
    
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    
    const rolePermissions = {
      super_admin: ['all'],
      admin: ['users:read', 'users:create', 'users:update', 'content:all'],
      editor: ['content:read', 'content:create', 'content:update'],
      teacher: ['content:read', 'gallery:create', 'news:create']
    };

    const permissions = rolePermissions[user.role] || [];
    
    if (permissions.includes('all')) return true;
    if (permissions.includes(permission)) return true;
    
    const category = permission.split(':')[0];
    if (permissions.includes(`${category}:all`)) return true;
    
    return false;
  };

  const canManageUsers = () => {
    return user?.role === 'super_admin' || user?.role === 'admin';
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    hasPermission,
    canManageUsers,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
